import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

import { cx } from '../atoms/cx.ts'

import { useDwell } from './useDwell.ts'

import type { ShakerFigureState, ShakerLatchShape, ShakerStop } from './ModuleFigures.tsx'
import type { RotorFigureProps } from './RotorFigure.tsx'
import {
  BODY_HEIGHT,
  BODY_TOP,
  cellAnchor,
  PLAN_CENTER_X,
  PLAN_HEIGHT,
  PLAN_WIDTH,
} from './trayGeometry.ts'
import {
  ASM_STATION_FIT_N,
  FIG_LABEL_GAP,
  FIG_LABEL_LINE,
  FIG_PART_GAP,
  figUnits,
  STAIN_FRONT_FIT_N,
} from './figureSpacing.ts'

import './figures.css'

/**
 * **통합 콘솔의 장비 그림 — 디자인팀 시안의 «선 도면»**(eric 2026-10-02 「그림들 다 수정해줘.. 디자인팀에서 제공한거」).
 *
 * 1차 자료: `docs/design/CTcells-console.html` 의 `rotorSVG()` · `asmSVG` · `shakerSVG` 와 도면 클래스
 * (`fig-l` 본체 · `fig-t` 부품 · `fig-d` 빈 자리 · `fig-f` 채운 부품 · `fig-x` 십자 · `fig-hair` 가는 선 · `ro-on` 적재).
 * 좌표는 시안 그대로이고, 색은 시안 값이 아니라 콘솔 토큰이다(`figures.css` 「콘솔 선 도면」 절).
 *
 * 🔴 **콘솔 스킨에서만 선다**(`RotorFigure` · `AssemblerFigure` · `ShakerFigure` 가 `hasSkin()` 으로 가른다 —
 *    `ChamberView` 와 같은 갈래). 장비 관점 콘솔 · 모듈 앱 3개 · 패드는 스킨이 없어 종전 그림 그대로다.
 * 🔴 **시안은 데모라 값을 박아 둔다**(로터 적재 `{3:'C1',4:'C2'}` 등). 여기는 **받은 값만** 그 표지로 그린다 —
 *    값이 없으면 시안의 «빈 자리»(점선 · 옅은 면)다. 「모른다」를 «있다»로 그리지 않는다.
 * 🔴 **색만으로 가르지 않는다** — 적재는 실선 흰 칸 + 칸 이름, 빈 자리는 점선, 진행은 굵은 테, 판정은 글리프다.
 */

/** 시안 `rotorSVG()` 좌표 — viewBox 300. [번호, x, y, 폭, 높이] · 1↔3 · 2↔4 가 마주보는 쌍이다 */
const ROTOR_SLOTS: Readonly<Record<1 | 2 | 3 | 4, readonly [number, number, number, number]>> = {
  1: [106, 22, 88, 58],
  2: [220, 106, 58, 88],
  3: [106, 220, 88, 58],
  4: [22, 106, 58, 88],
}

/**
 * 원심 로터 — 시안 `rotorSVG()`. 바깥 원 · 옅은 십자 · 네 자리(둥근 사각) · 가운데 축 + 가는 테.
 * 자리 번호는 «자리 안 왼쪽 위», 칸 이름은 자리 가운데.
 * 🔴 회전 중이어도 도형이 안 바뀐다 — 바깥 점선 링만 생긴다(종전 규약 그대로).
 */
export function RotorLine({ slots, spinning = false, size = 'md', label }: RotorFigureProps) {
  return (
    <div
      aria-label={label}
      className={cx('fig', 'fig-rotor', 'fig-line', size !== 'md' && `fig-rotor--${size}`)}
      role="img"
    >
      <svg aria-hidden="true" className="fig-rotor__plan" viewBox="0 0 300 300">
        {/*
         * 🔴 도는 동안 바퀴(면 · 십자 · 칸 · 허브)가 통째로 돈다(eric 2026-10-07 「원심 분리기 돌 때 회전하는 효과」).
         *    `spinning` 은 서버 트레이 뷰의 「회전 중」(`c_r{k}_spin` 스텝 진행 중)이다 — 싣고 꺼내는 동안은 돌지 않는다.
         *    움직임은 토큰 `--dur-spin` 이 쥔다(`prefers-reduced-motion` 이면 0ms → 멈춘 그림).
         */}
        <g
          className={cx('fig-rotor__wheel', spinning && 'fig-rotor__wheel--spinning')}
          data-spinning={spinning ? 'true' : undefined}
        >
          {spinning ? <circle className="fig-line__spin" cx={150} cy={150} r={156} /> : null}
          <circle className="fig-line__body fig-line__body--face" cx={150} cy={150} r={144} />
          <path className="fig-line__cross" d="M150 80V220M80 150H220" />
          {slots.map((slot) => {
            const [x, y, w, h] = ROTOR_SLOTS[slot.index]
            /* 적재 여부는 받은 값(`loaded`) 그대로 · 칸 이름은 있을 때만 적는다 */
            const loaded = slot.loaded
            return (
              <g
                data-counterweight={slot.counterweight === true ? 'true' : undefined}
                data-rotor-slot={slot.index}
                data-loaded={loaded ? 'true' : 'false'}
                key={slot.index}
              >
                {slot.flagged === true ? (
                  <rect
                    className="fig-line__flag"
                    height={h + 10}
                    rx={10}
                    width={w + 10}
                    x={x - 5}
                    y={y - 5}
                  />
                ) : null}
                <rect
                  className={cx(
                    loaded ? 'fig-line__on' : 'fig-line__empty',
                    loaded && slot.active === true && 'fig-line__on--now',
                    /* 평형추 자리 — 트레이 RSLOT 의 R″ 칸과 같은 채운 면(챔버 칸과 가른다) */
                    loaded && slot.counterweight === true && 'fig-line__cw',
                  )}
                  height={h}
                  rx={7}
                  width={w}
                  x={x}
                  y={y}
                />
                <text className="fig-line__num" x={x + 8} y={y + 16}>
                  {slot.index}
                </text>
                {loaded && slot.slot !== undefined ? (
                  <>
                    <text
                      className="fig-line__lab"
                      dominantBaseline="central"
                      textAnchor="middle"
                      x={x + w / 2}
                      y={y + h / 2 + (slot.sublabel === undefined ? 2 : -6)}
                    >
                      {slot.slot}
                    </text>
                    {slot.sublabel === undefined ? null : (
                      <text
                        className="fig-line__sub"
                        dominantBaseline="central"
                        textAnchor="middle"
                        x={x + w / 2}
                        y={y + h / 2 + 14}
                      >
                        {slot.sublabel}
                      </text>
                    )}
                  </>
                ) : null}
                {slot.verdict ? (
                  <>
                    <circle
                      className={cx(
                        'fig-line__badge',
                        slot.verdict === 'fail' && 'fig-line__badge--fail',
                      )}
                      cx={x + w}
                      cy={y}
                      r={10}
                    />
                    <text
                      className={cx(
                        'fig-line__badgetxt',
                        slot.verdict === 'fail' && 'fig-line__badgetxt--fail',
                      )}
                      dominantBaseline="central"
                      textAnchor="middle"
                      x={x + w}
                      y={y + 1}
                    >
                      {slot.verdict === 'fail' ? '✗' : '✓'}
                    </text>
                  </>
                ) : null}
              </g>
            )
          })}
          <circle className="fig-line__solid" cx={150} cy={150} r={9} />
          <circle className="fig-line__hair" cx={150} cy={150} r={16} />
        </g>
      </svg>
    </div>
  )
}

/** 시안 `asmSVG` 높이(170) + 아래 상태 한 줄 — 장비 탭 맞춤 배율이 이 높이를 센다(`figures.css` `--fig-fit-n`) */
const ASM_H = 192

/**
 * 어셈블러 평면도 — 시안 `asmSVG`. 둥근 본체 · 프레임 기둥 2 · UV 램프 하우징 · 틸트 스테이지(+ 축 가는 선) · 구동부 · 구멍 2.
 *
 * | 값 | 그림 |
 * | --- | --- |
 * | UV 꺼짐 | 하우징이 시안 그대로 점선 빈 자리 |
 * | UV 켜짐 | 하우징이 실선 + 아래로 빛줄기(가는 점선) — 모양이 말한다 |
 * | 스테이지 위 칸 | 받은 칸 이름이 있을 때만 흰 칸(실선) + 칸 이름. 없으면 빈 스테이지(시안 그대로) |
 */
export function AssemblerLine({
  uvOn,
  chamberSlot,
  uvLabel,
  label,
}: {
  uvOn: boolean
  chamberSlot?: string | undefined
  uvLabel: string
  label: string
}) {
  return (
    <svg
      aria-label={label}
      className="fig fig-mod fig-mod--asm fig-line"
      data-uv={uvOn ? 'on' : 'off'}
      height={ASM_H}
      role="img"
      viewBox={`0 0 200 ${String(ASM_H)}`}
      width={200}
    >
      <rect className="fig-line__body" height={158} rx={14} width={192} x={4} y={4} />
      <rect className="fig-line__part" height={138} rx={3} width={12} x={14} y={14} />
      <rect className="fig-line__part" height={138} rx={3} width={12} x={174} y={14} />
      {/* 켜지면 램프가 숨 쉬듯 밝아졌다 어두워지고 빛줄기가 아래로 흐른다(`figures.css` fig-uv-glow · fig-uv-flow · 토큰 `--dur-pulse`) */}
      <rect
        className={uvOn ? 'fig-line__on fig-line__uv--on' : 'fig-line__empty'}
        data-part="uv"
        height={20}
        rx={2}
        width={130}
        x={34}
        y={18}
      />
      {uvOn ? (
        <path
          className="fig-line__ray"
          d="M56 42v12M78 42v12M99 42v12M120 42v12M142 42v12"
          data-part="uv-ray"
        />
      ) : null}
      <rect className="fig-line__empty" height={70} rx={4} width={96} x={50} y={62} />
      <path className="fig-line__hair" d="M52 97H144" />
      {chamberSlot === undefined ? null : (
        <g data-part="stage-chamber">
          <rect className="fig-line__on" height={32} rx={5} width={48} x={74} y={81} />
          <text
            className="fig-mod__txt fig-line__lab fig-line__lab--sm"
            dominantBaseline="central"
            textAnchor="middle"
            x={98}
            y={98}
          >
            {chamberSlot}
          </text>
        </g>
      )}
      <rect className="fig-line__solid" height={58} rx={2} width={16} x={152} y={68} />
      <circle className="fig-line__part" cx={33} cy={143} r={5} />
      <circle className="fig-line__part" cx={164} cy={143} r={5} />
      <text className="fig-mod__txt" data-part="status" textAnchor="middle" x={100} y={ASM_H - 6}>
        {uvLabel}
      </text>
    </svg>
  )
}

/**
 * DH-Robotics **RGD-35-30** 회전 그리퍼 정면 — 거꾸로 매단 모습(eric 2026-10-08 「어셈블러 쪽 그리퍼 저 모델 이미지 찾아서 저렇게 구현해줘」 ·
 * 제조사 사진 `dh-robotics.com` RGD 시리즈). 위에서부터 Z 축 팔 · 장착 띠 · 본체(표시등 둘) · 회전부 · 손가락 받침 · 손가락 둘.
 * 쥐면 손가락이 오므라든다. 디테일(나사 · 각인 · 케이블)은 그리지 않는다.
 * 🔴 **원심분리기 · 어셈블러가 같은 그리퍼를 쓴다**(`profiles/{centrifuge,assembler}.yaml` `end_effector.spec.model`) — 그림도 이 한 벌이다.
 * 좌표는 가운데 x 100 · 팔 위 y 10 · 손가락 끝 y 71(그리는 쪽이 `viewBox` 나 묶음 이동으로 자리를 잡는다)
 */
export const RGD_TIP_Y = 71
export function RgdGripperGlyph({ down, holding }: { down: boolean; holding: boolean }) {
  return (
    <g data-part="rgd-gripper">
      <rect className="fig-line__part" data-part="arm" height={10} rx={2} width={6} x={97} y={10} />
      <rect
        className="fig-line__part"
        data-part="grip-cap"
        height={4}
        rx={1}
        width={30}
        x={85}
        y={20}
      />
      <rect
        className={down ? 'fig-line__on' : 'fig-line__part'}
        data-part="press-tip"
        height={22}
        rx={2}
        width={28}
        x={86}
        y={24}
      />
      {/* 표시등 둘 — 본체 아래쪽 */}
      <rect
        className="fig-line__part"
        data-part="grip-led"
        height={2}
        rx={1}
        width={2}
        x={92}
        y={41}
      />
      <rect
        className="fig-line__part"
        data-part="grip-led"
        height={2}
        rx={1}
        width={2}
        x={97}
        y={41}
      />
      <rect
        className="fig-line__part"
        data-part="grip-rotor"
        height={8}
        rx={1}
        width={26}
        x={87}
        y={46}
      />
      <rect
        className="fig-line__part"
        data-part="grip-base"
        height={5}
        rx={1}
        width={20}
        x={90}
        y={54}
      />
      <rect
        className="fig-line__part"
        data-part="finger"
        height={12}
        rx={1}
        width={6}
        x={holding ? 93 : 90}
        y={59}
      />
      <rect
        className="fig-line__part"
        data-part="finger"
        height={12}
        rx={1}
        width={6}
        x={holding ? 101 : 104}
        y={59}
      />
    </g>
  )
}

/**
 * **피펫 끝장치(Hamilton ZEUS LT 정면)** — 판 위 겹침 3축 도구(`SheetGripper` · 염색기)의 끝장치다. 3축은 원심 · 어셈블러와 같고 끝장치만 다르다
 * (eric 2026-10-13 「엔드 이펙터만 피펫으로 다른 거지.. 사실 3축은 같거든」). 좌표는 그리퍼(`RgdGripperGlyph`)와 같은 틀 — 가운데 x 100 · 팔 위 y 10 ·
 * 틀 `82 8 36 84` 안 — 이라 겹침 도구의 크기 · 굵기 · 자리 규칙을 그대로 탄다. 모양은 염색기 정면도의 피펫 툴(장착 블록 · 커넥터 · 본체 · 표시등 ·
 * 고리 · 배럴 · 팁)을 줄인 것이다. 팁은 서버 `tip.mounted` 가 `true` 일 때만 · 액체 방향(흡입 ↑ · 분주 ↓)은 내려와 있을 때만.
 */
export const ZEUS_NOZZLE_END_Y = 78
export function ZeusPipetteGlyph({
  down,
  tipMounted,
  arrow,
}: {
  down: boolean
  tipMounted: boolean
  arrow?: 'up' | 'down' | undefined
}) {
  return (
    <g data-part="zeus-pipette">
      <rect
        className="fig-line__part"
        data-part="pip-arm"
        height={8}
        rx={2}
        width={6}
        x={97}
        y={10}
      />
      <rect
        className="fig-line__part"
        data-part="pip-connector"
        height={4}
        rx={1}
        width={3}
        x={89}
        y={15}
      />
      <rect
        className={down ? 'fig-line__on' : 'fig-line__part'}
        data-part="pip-body"
        height={40}
        rx={3}
        width={16}
        x={92}
        y={18}
      />
      <circle className="fig-line__part" cx={96} cy={48} data-part="pip-led" r={1.6} />
      <rect
        className="fig-line__part"
        data-part="pip-collar"
        height={3}
        rx={1}
        width={6}
        x={97}
        y={58}
      />
      <rect
        className="fig-line__part"
        data-part="nozzle"
        height={17}
        rx={1}
        width={3}
        x={98.5}
        y={61}
      />
      {tipMounted ? (
        <path
          className="fig-line__solid"
          d={`M98.5 ${String(ZEUS_NOZZLE_END_Y)}l1.5 13 1.5-13z`}
          data-part="tip"
        />
      ) : null}
      {arrow === undefined || !down ? null : (
        <path
          className="fig-line__hair fig-line__nudge"
          d={arrow === 'up' ? 'M113 50V30M110 33l3-3 3 3' : 'M113 30v20M110 47l3 3 3-3'}
          data-dir={arrow}
          data-part="flow-arrow"
        />
      )}
    </g>
  )
}

/** 어셈블러 장치의 지금 — 서버 모듈 상태에서 부르는 쪽이 고른다(`processSteps.stationPhaseOf`) */
export type StationPhase = 'idle' | 'load' | 'uv' | 'remove'

/** 3축 갠트리가 선 자리(모델 `asmStationModel.ts` 가 서버 사실로 고른다). ⚠ 그림은 2026-10-08 오후부터 좌우 이동을 안 그린다 — 자리는 상세 글자 · 시험에만 남는다 */
export type GantryStop = 'rack' | 'stage' | 'waste'

/**
 * 스테이지 위 챔버(그림 좌표) — 🔴 **TC 랙 한 칸보다 조금 큰 크기**다(eric 2026-10-11 「챔버 크기가 너무 커.. 좀 작게 해줘」).
 *    종전 48 × 30 은 TC 랙 사각형 «통째»(46 × 30)만 했다 — 실물은 챔버 하나가 TC 하나(랙 칸 10 × 6)를 받는 크기다.
 *    받침(스테이지 판) · TC 틀(U 자) · 누름판 · UV 램프 · 그리퍼 내려오는 거리가 전부 이 값에서 나온다(아래 상수).
 */
const ASM_CHAMBER = { w: 20, h: 12 } as const
/** 스테이지 판 윗변(그림 좌표) — 챔버가 이 위에 얹힌다. TC 랙 · 폐기통 바닥(y 134)과 판 아랫변이 같은 높이 */
const ASM_STAGE_TOP = 128
/** 스테이지 판(받침) — 🔴 챔버보다 넓다(챔버를 받치는 판). 가운데 x 100 */
const ASM_STAGE = { w: 36, h: 6 } as const
/** 챔버 윗변 = 판 윗변 − 챔버 키 */
const ASM_CHAMBER_TOP = ASM_STAGE_TOP - ASM_CHAMBER.h
/** TC(두께 조절기) U 자 틀 — 챔버를 감싼다(좌우 3 · 위 3 여유) · 아래는 판 안까지 */
const ASM_TC = {
  w: ASM_CHAMBER.w + 6,
  top: ASM_CHAMBER_TOP - 3,
  bottom: ASM_STAGE_TOP + 2,
} as const
/** 누름판 폭 — TC 틀 폭에 맞춘다. 누를 때 판 아랫변이 TC 윗변에 닿는다(`PRESS_TRAVEL`) */
const ASM_PRESS_W = ASM_TC.w + 4
/** 누름판 아랫변(올라간 채) · 내려가는 거리 — CSS `.fig-line__press[data-down]` 의 translateY 와 같은 값 */
const PRESS_TRAVEL = 10
const ASM_PRESS_BOTTOM = ASM_TC.top - PRESS_TRAVEL
/** 그리퍼 손가락 끝(올라간 채) — 든 TC(`held-tc`)의 윗변 */
const GRIP_TIP_Y = 70
/**
 * 그리퍼(누름 헤드)가 내려온 거리(그림 좌표) — 손가락 끝이 TC 윗변에 닿는 거리. CSS `translateY` 와 같은 값
 * (`figures.css` `.fig-line__head[data-down]`). 챔버가 작아져(낮아져) 종전 30 → 43
 */
const HEAD_TRAVEL = ASM_TC.top - GRIP_TIP_Y
/** 3축 그리퍼의 X — 스테이지 위 · 떨어진 자리(왼쪽). 묶음은 스테이지 자리에 그려 두고 `translateX` 로 옮긴다 */
/**
 * 어셈블러 장치 정면도의 보이는 틀 — 내용(팔 · 그리퍼 · 누름판 · 스테이지 · 램프 · 상태 한 줄)에 맞게 잘랐다. 종전 200 × 192 틀은 본체 사각형이
 * 있던 자리라 내용이 가운데 작게 섰다(eric 2026-10-08 「어셈블러의 크기가 너무 작아.. 크기 좀 키워줘」). 장비 판 배율은 `figures.css` `.fig-asmstation`
 */
/**
 * 🔴 **여백 규칙**(2026-10-11 · eric 「전체 균형을 맞춰줘.. 여백 같은 거」) — 부품 사이 · 이름표 틈은 장비 판 공용 기준 px(`figureSpacing.ts`)를
 *    이 그림의 맞춤 높이(107)로 환산한 값이다. 종전엔 TC 랙 ↔ 받침 20 · 받침 ↔ 폐기통 26 · 이름표 9(글자 가운데) 로 따로 놀았다.
 */
const ASM_PART_GAP = figUnits(FIG_PART_GAP, ASM_STATION_FIT_N)
const ASM_LABEL_GAP = figUnits(FIG_LABEL_GAP, ASM_STATION_FIT_N)
/** 틀 바깥 여백(그림 좌표) — 왼쪽(TC 랙 앞) · 오른쪽(폐기통 뒤)이 같다 */
const ASM_EDGE = 6
const ASM_STAGE_LEFT = 100 - ASM_STAGE.w / 2
const ASM_STAGE_RIGHT = 100 + ASM_STAGE.w / 2
/** TC 랙 · 폐기통 상자 크기(그림 좌표) — 둘이 같다 */
const ASM_BOX = { w: 46, h: 30, y: 104 } as const
const ASM_STATION_LEFT = ASM_STAGE_LEFT - ASM_PART_GAP - ASM_BOX.w - ASM_EDGE
/** 폐기통이 없을 때의 오른쪽 끝 — 누름 장치 · 화살표까지(종전 150) */
const ASM_STATION_RIGHT = 150
const ASM_STATION_BOX = {
  x: ASM_STATION_LEFT,
  y: 6,
  w: ASM_STATION_RIGHT - ASM_STATION_LEFT,
  h: 160,
} as const
const GANTRY_STAGE_X = 100
const GANTRY_PARK_X = 36
/**
 * TC 랙 칸 수 — 그림의 도식 칸이다(`TcRackFigure` 와 같은 3 × 3). 남은 수(`tc_remaining`)만 서버 사실이고 칸은 앞에서부터 비운다.
 * ⚠ 어셈블러 장치 정면도 왼쪽의 TC 랙(eric 2026-10-08 「어셈블러도 염색기처럼 왼쪽에 tc rack 사각형으로 그려줘」 — 같은 날 오전에 걷었던 것을 되살렸다)
 */
export const TC_RACK_SLOTS = 9
/**
 * 🔴 **콘솔 장비 판 배치 — 왼쪽 어셈블러 장치 · 오른쪽 한 열(위 TC rack · 아래 폐기통)**(eric 2026-10-19 「왼쪽에는 어셈블러.. 오른쪽 위쪽에는 TC rack
 *    오른쪽 아래에는 폐기로 해. 그러니까 두 열로」 · 「염색기와 비교해서.. 어셈블러도.. 사각형 컴포넌트와 텍스트 크기 통일」). TC 랙 · 폐기통 이름표를
 *    넘기는 판(콘솔)만 이 배치다 — 종전 좌(TC 랙) · 우(폐기통) 배치는 이름표가 없는 판(장비 관점 앱 · 패드)에 남는다.
 * 상자는 염색기 통 칸(`BIN_W × BIN_H` · 염색기 맞춤 높이)과 **화면에서 같은 크기**다 — 두 그림의 맞춤 높이 비로 환산한다(`figureSpacing.ts`).
 * 열의 위 · 아래는 왼쪽 장치에 맞춘다(아래 `ASM_DEVICE_TOP` · `ASM_DEVICE_BOTTOM`)
 */
/**
 * 맞춤 높이 비만으로는 화면 크기가 안 맞는다 — 장비 판은 그림마다 칸 폭에 한 번 더 맞춰 줄인다(`--fit-zoom`). 염색기 그림이 가로로 길어(트레이 + 통 열)
 * 더 줄어서, 같은 환산 값이 어셈블러에서 약 1.6 배로 섰다(1920 · 2026-10-19 실측: 염색기 통 50 × 36px · 어셈블러 79 × 57px). 그 비(0.63)를 곱한다
 */
const ASM_COL_FIT = 0.63
const ASM_COL_BOX = {
  w: Math.round(((96 * ASM_STATION_FIT_N) / STAIN_FRONT_FIT_N) * ASM_COL_FIT * 100) / 100,
  h: Math.round(((70 * ASM_STATION_FIT_N) / STAIN_FRONT_FIT_N) * ASM_COL_FIT * 100) / 100,
} as const
const ASM_COL_X = ASM_STAGE_RIGHT + ASM_PART_GAP
/**
 * 열의 위 · 아래 = 왼쪽 장치의 위 · 아래(eric 2026-10-19 「오른쪽에 tc rack 와 폐기통.. 왼쪽에.. 어셈블러 높이를 맞춰줘」) — TC rack 윗변 = 누름 장치 팔 윗변,
 * 폐기통 아랫변 = 스테이지 밑 UV 램프 아랫변. 두 상자 사이 틈에 TC rack 이름표가 든다
 */
const ASM_DEVICE_TOP = ASM_PRESS_BOTTOM - 18
const ASM_DEVICE_BOTTOM = ASM_STAGE_TOP + ASM_STAGE.h + 8
const ASM_COL_TC_Y = ASM_DEVICE_TOP
const ASM_COL_WASTE_Y = ASM_DEVICE_BOTTOM - ASM_COL_BOX.h
/** 이름표 틈 · 글자 — 상자와 같은 비로 줄인다(염색기 통과 화면에서 같은 틈 · 같은 글자) */
const ASM_COL_LABEL_GAP = ASM_LABEL_GAP * ASM_COL_FIT
/** 열 배치의 틀 왼쪽 · 오른쪽 — 스테이지 앞 여백 · 열 뒤 여백이 같다(그림 안 그리퍼가 없는 판) */
const ASM_COL_LEFT = ASM_STAGE_LEFT - ASM_EDGE
const ASM_COL_RIGHT = ASM_COL_X + ASM_COL_BOX.w + ASM_EDGE

/** 갠트리 자리 · 내려감이 한 번 바뀌면 최소 이렇게는 보여 준다(염색기 툴 · 6축과 같은 규칙) */
export const ASM_DWELL_MS = 1500

/**
 * 어셈블러 장치 칸 — **정면도 한 장 · 움직인다**: 엔드 이펙터만 보이는 3축 그리퍼(RGD-35-30 · 본체 + 회전부 + 손가락 둘) · 스테이지 위 압착 장치(팔 + 누름판), Z 스테이지(챔버) 위 TC 틀, **스테이지 밑 UV 램프**(아래에서 쏜다 · eric 2026-10-08).
 * 🔴 본체 사각형 · 좌우 기둥 · TC 랙 · 폐기통 · 좌우 이동은 2026-10-08 오후에 걷었다(eric 「TC 랙, 폐기통 이거랑 기둥들 다 빼줘. 둘러싸고 있는 사각형도 빼고」) — 되살리지 마라.
 * (eric 2026-10-07 「압착 부분이랑 UV 노출 부분.. 두개.. 합쳐달라고 했잖아.. 정면에서」 → 「동영상 형태로 구현해줘」 → 2026-10-08 「3축 로봇.. 여기서는..
 * tc rack 을 놓고 빼는 거 보여줘」). 종전 평면도 두 장(압착부 · 콘솔의 `AssemblerLine`)을 이 한 장이 대신한다 — 틀 · 배율(200 × `ASM_H`)은 그대로.
 *
 * | 서버 사실 → `phase` · `down` · `holding` | 그림 |
 * | --- | --- |
 * | 그 밖(`idle`) | 그리퍼가 올라간 채 · TC 틀 점선 · 램프 점선 |
 * | `TC_LOAD` → `load` · 빈손 | TC 를 집으러 — `grip` 이면 내려간다 · ↓ 까딱 |
 * | `TC_LOAD` → `load` · 쥠(`grips[].state = holding`) | TC 를 든 채(그리퍼 끝 U 자) · `release` 면 내려가 챔버에 얹는다 · 얹으면 TC 틀 실선 |
 * | `UV` → `uv` | 그리퍼는 떨어진 자리에 올라간 채 · 누름판이 누른 채 · 램프 실선 + 숨 쉬는 밝기 · 판을 지나 **위로** 흐르는 빛줄기 |
 * | `TC_REMOVE` → `remove` | `grip` 이면 내려가 TC 를 집는다 · 쥐면 든 채 ↑ 까딱(폐기통은 안 그린다 · 레시피 `placedown waste_container`) |
 * | 스테이지 위 칸 | 받은 칸 이름이 있을 때만 흰 칸 + 칸 이름 |
 *
 * 🔴 **움직임은 서버 사실의 «변화»다** — 자리는 상태 · 파지 · 동작 이름이 바뀔 때 토큰 시간만큼 이동하고(CSS transition), 그 안에서는 까딱임 · 숨쉬기만 한다.
 *    시간을 재거나 다음 동작을 짐작해 미리 움직이지 않는다. 바뀜이 너무 빠르면 `ASM_DWELL_MS` 만큼은 머문 뒤 옮긴다(지어내지 않는다 — 조금 늦게 보여 줄 뿐).
 *    모션은 전부 토큰(`--dur-move` · `--dur-pulse`)이라 reduced-motion 이면 멈춘 그림.
 * 🔴 색만으로 말하지 않는다 — 모양(점선/실선 · 자리 · 빛줄기 · 든 TC)과 아래 한 줄 글자가 같이 말한다. 어느 챔버를 누르는지는 그리지 않는다(BR-98).
 * `down` · `holding` 을 안 넘기면 종전 규칙(상태만 · `idle` 아니면 내려옴)이다.
 */
export function AssemblerStationLine({
  phase,
  uvOn,
  chamberSlot,
  label,
  statusLabel,
  down: downNow,
  holding: holdingNow = false,
  gripper = true,
  fitDevice = false,
  tcRemaining,
  tcControl,
  tcLabel,
  wasteLabel,
  chamberLabel = true,
}: {
  tcControl?: { label: string; onClick: () => void } | undefined
  phase: StationPhase
  /** 서버 트레이 뷰 · 텔레메트리의 「UV 켜짐」 — `phase === 'uv'` 와 둘 중 하나면 램프가 켜진다 */
  uvOn: boolean
  chamberSlot?: string | undefined
  label: string
  /** 아래 한 줄 — 서버 상태 이름(TC 장착 · UV 노출 · TC 분리) 또는 빈 글자 */
  statusLabel: string
  /** 그리퍼가 내려가 있는가(서버 동작 `grip` · `release`). 없으면 장착 · 분리 중 */
  down?: boolean | undefined
  /** TC 를 쥐고 있는가(서버 `grips[].state === 'holding'`) */
  holding?: boolean | undefined
  /**
   * 그림 안 그리퍼를 그리는가 — 콘솔 장비 판은 끈다. 판 전체 위를 오가는 겹침 그리퍼(`AsmGripper`)가 대신한다
   * (eric 2026-10-08 「어셈블러도 원심분리기처럼 움직이고」)
   */
  gripper?: boolean | undefined
  /** 모듈 화면의 장치 칸만 꺼진 그리퍼의 빈 윗공간을 덜어 자동 맞춤 크기를 키운다. */
  fitDevice?: boolean | undefined
  /** TC 랙의 남은 수(모듈 소모품 `tc_remaining`) — 넘기면 왼쪽에 TC 랙을 그린다. 모르면 칸을 점선으로 */
  tcRemaining?: number | null | undefined
  /** TC 랙 이름표(「TC 랙」) — 랙 «바깥» 왼쪽 아래(염색기 팁 랙과 같은 자리) */
  tcLabel?: string | undefined
  /** TC 폐기통 이름표(「폐기통」) — 넘기면 스테이지 오른쪽에 폐기통을 그린다. 겹침 그리퍼가 TC 를 버리러(`tc_waste`) 그 위로 간다 */
  wasteLabel?: string | undefined
  /**
   * 스테이지 챔버 옆 칸 이름(「A3」)을 그리는가(기본 `true`). 종전에는 겹침 그리퍼가 «그 챔버를» 쥐면 칩이 이름을 말해서
   *    부르는 쪽이 `false` 를 넘겼다(eric 2026-10-11 「같은 이름 두 번」). 겹침 그리퍼가 2026-10-14 에 걷혀 지금은 `false` 를 넘기는 곳이 없다
   */
  chamberLabel?: boolean | undefined
}) {
  const shown = useDwell(
    /* 안 넘기면 장착 · 분리 중에만 내려옴 — UV 는 압착 장치가 누르는 구간이라 그리퍼는 올라간 채 떨어져 있다 */
    { down: downNow ?? (phase === 'load' || phase === 'remove'), holding: holdingNow },
    ASM_DWELL_MS,
    (a, b) => a.down === b.down && a.holding === b.holding,
  )
  const { down, holding } = shown
  const lampOn = uvOn || phase === 'uv'
  /*
   * TC 틀(챔버를 감싼 U 자) — TC 가 챔버에 얹힌 구간(장착 뒤 · UV · 분리 전)에 실선. 들고 있는 중에는 그리퍼 손가락 사이에 있다.
   * 🔴 장착과 분리는 그림이 다르다(eric 2026-10-08 「tc 분리하는 거랑 연결하는 거 그림을 다르게」):
   *   · 장착(`load` · 쥠) = TC 를 들고 **내려가는 중** — 챔버 위 틀은 아직 없음(점선 없음) · 손가락에서 챔버까지 점선 안내선 · ↓↓
   *   · 분리(`remove` · 쥠) = TC 를 **들어 올린 뒤** — 챔버 위에는 빠져나간 자리(점선 틀) · ↑↑
   */
  const tcOnStage = phase === 'uv' || (phase !== 'idle' && !holding)
  const tcMotion = holding ? (phase === 'remove' ? 'removing' : 'placing') : undefined
  /*
   * 압착 장치(Z 스테이지 프레스) — 3축 그리퍼가 TC 를 얹고 물러난 뒤 **누름판이 내려와 TC 를 챔버에 압착**한다 · UV 동안 누른 채
   * (eric 2026-10-08 「tc 는 3축 로봇이 하고 압착은.. 어셈블러가 압착하는 장비는 빠져 있어」). 그리퍼가 내려와 있는 동안은 올라가 있다(두 장치가 겹치지 않게)
   */
  const pressing = phase === 'uv' || (phase === 'load' && !holding && !down)
  /*
   * 3축 그리퍼의 자리 — 평소엔 **떨어진 자리(왼쪽)** 에 있다가 TC 를 들고 오거나 집으러 올 때만 스테이지 위로(eric 2026-10-08 「3축 로봇은 좀 더
   * 떨어진 곳에 있다가 tc 전달하고」). 분리한 TC 를 들면 다시 떨어진 자리로(들고 나간다). 이동은 토큰 시간(`fig-line__gantry`)
   */
  const atStage = down || (holding && phase !== 'remove')
  const gx = atStage ? GANTRY_STAGE_X : GANTRY_PARK_X
  const press = tcOnStage ? 'fig-line__on' : 'fig-line__empty'
  const lamp = lampOn ? 'fig-line__on fig-line__uv--on' : 'fig-line__empty'
  const arrowDir = phase === 'load' ? 'down' : phase === 'remove' ? 'up' : undefined
  /* 오른쪽 한 열 배치(콘솔) — 이름표를 넘긴 판. 그림 안 그리퍼가 서는 판은 떨어진 자리(왼쪽)까지 틀을 남긴다 */
  const column = tcLabel !== undefined || wasteLabel !== undefined
  const boxX = column ? (gripper ? ASM_STATION_BOX.x : ASM_COL_LEFT) : ASM_STATION_BOX.x
  const boxW = column ? ASM_COL_RIGHT - boxX : ASM_STATION_BOX.w
  const boxY = fitDevice && !gripper ? ASM_DEVICE_TOP - ASM_EDGE : ASM_STATION_BOX.y
  const boxH = ASM_STATION_BOX.y + ASM_STATION_BOX.h - boxY
  return (
    <svg
      aria-label={label}
      className="fig fig-mod fig-mod--asm fig-line fig-asmstation"
      data-holding={holding ? 'true' : 'false'}
      data-phase={phase}
      data-press={down ? 'on' : 'off'}
      data-pressing={pressing ? 'on' : 'off'}
      data-tc={tcMotion ?? (tcOnStage ? 'seated' : 'none')}
      data-uv={lampOn ? 'on' : 'off'}
      data-view="front"
      height={boxH}
      /*
       * 🔴 TC 랙이 누를 수 있는 버튼이면(모듈 앱 · `tcControl`) 그림을 `img` 로 두지 않는다 — `img` 의 자식은 접근성 트리에서 지워져
       *    스크린리더 · 음성 조작이 그 버튼을 못 찾는다. 그때는 이름 붙은 묶음(`group`)으로 선다 — 선 · 면은 이름이 없어 트리에 안 서고,
       *    화면에 보이는 글자(폐기통 · 칸 이름 · 상태)만 그대로 읽힌다. 버튼이 없으면(통합 콘솔) 종전대로 `img` 한 장이다
       */
      role={tcControl === undefined ? 'img' : 'group'}
      viewBox={`${String(boxX)} ${String(boxY)} ${String(boxW)} ${String(boxH)}`}
      width={boxW}
    >
      {wasteLabel === undefined ? null : (
        /*
         * TC 폐기통 — 오른쪽 열 아래(콘솔) · 염색기 폐기통과 같은 단순형(가운데 바닥 구멍 + 깔때기 사선 · eric 2026-10-19 「폐기통도.. 마찬가지로 중앙에
         * 사각형」). 이름표는 «바깥» 왼쪽 아래 · 염색기 통 이름표와 같은 글자
         */
        <g data-part="tc-waste">
          <rect
            className="fig-tray__body"
            height={ASM_COL_BOX.h}
            rx={(6 * ASM_COL_BOX.w) / BIN_W}
            width={ASM_COL_BOX.w}
            x={ASM_COL_X}
            y={ASM_COL_WASTE_Y}
          />
          <WasteBinTop h={ASM_COL_BOX.h} w={ASM_COL_BOX.w} x={ASM_COL_X} y={ASM_COL_WASTE_Y} />
          <text
            className="fig-line__note"
            data-part="tc-waste-label"
            dominantBaseline="hanging"
            textAnchor="start"
            x={ASM_COL_X}
            y={ASM_COL_WASTE_Y + ASM_COL_BOX.h + ASM_COL_LABEL_GAP}
          >
            {wasteLabel}
          </text>
        </g>
      )}
      {/* 🔴 본체 사각형 · 좌우 기둥 · 바닥 구멍 · 위 긴 빔은 걷었다(eric 2026-10-08 「기둥들 다 빼줘.. 둘러싸고 있는 사각형도 빼고」 → 「위에 긴 사각형 없애」). 그리퍼 하우징(작은 사각형)만 남는다 */}
      {/* UV 램프 — **스테이지 아래**에서 위로 쏜다(eric 2026-10-08 「uv 가 밑에서 쏘는 걸로 아는데」). 판 밑 좌우 두 토막 · 빛줄기는 판을 지나 챔버 바닥으로 */}
      <rect
        className={lamp}
        data-part="uv"
        height={8}
        rx={2}
        width={9}
        x={100 - ASM_STAGE.w / 2 + 7}
        y={ASM_STAGE_TOP + ASM_STAGE.h}
      />
      <rect
        className={lamp}
        data-part="uv"
        height={8}
        rx={2}
        width={9}
        x={100 + ASM_STAGE.w / 2 - 16}
        y={ASM_STAGE_TOP + ASM_STAGE.h}
      />
      {lampOn ? (
        <path
          className="fig-line__ray"
          d={[-6, -2, 2, 6]
            .map(
              (dx) =>
                `M${String(100 + dx)} ${String(ASM_STAGE_TOP + 4)}v-${String(ASM_CHAMBER.h - 2)}`,
            )
            .join('')}
          data-part="uv-ray"
        />
      ) : null}
      {tcLabel === undefined ? null : (
        /*
         * TC 랙 — 오른쪽 열 위(콘솔) · **외곽 테두리만**(eric 2026-10-19 「TC 도 외곽 테두리만」 · 실물: 왼쪽 귀 둥긂 + 손잡이 구멍). 남은 수 · 칸 상태는
         * 소모품 판이 말한다. 칸 9 개는 «보이지 않는 표지»로만 남는다(`data-tc-slot` — 겹침 그리퍼가 그 칸 위로 간다)
         */
        <g
          data-part="tc-rack"
          role={tcControl === undefined ? undefined : 'button'}
          tabIndex={tcControl === undefined ? undefined : 0}
          aria-label={tcControl === undefined ? undefined : `${tcLabel} ${tcControl.label}`}
          aria-haspopup={tcControl === undefined ? undefined : 'dialog'}
          onClick={tcControl?.onClick}
          onKeyDown={
            tcControl === undefined
              ? undefined
              : (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    tcControl.onClick()
                  }
                }
          }
          data-remaining={typeof tcRemaining === 'number' ? tcRemaining : undefined}
        >
          <TcRackOutline h={ASM_COL_BOX.h} w={ASM_COL_BOX.w} x={ASM_COL_X} y={ASM_COL_TC_Y} />
          {Array.from({ length: TC_RACK_SLOTS }, (_unused, n) => {
            const col = 2 - Math.floor(n / 3)
            const row = n % 3
            const sw = (ASM_COL_BOX.w * 0.74) / 3
            const sh = (ASM_COL_BOX.h * 0.86) / 3
            return (
              <rect
                className="fig-line__anchor"
                data-tc-slot={n}
                height={Math.round(sh * 100) / 100}
                key={n}
                width={Math.round(sw * 100) / 100}
                x={Math.round((ASM_COL_X + ASM_COL_BOX.w * 0.24 + col * sw) * 100) / 100}
                y={Math.round((ASM_COL_TC_Y + ASM_COL_BOX.h * 0.07 + row * sh) * 100) / 100}
              />
            )
          })}
          <text
            className="fig-line__note"
            data-part="tc-rack-label"
            dominantBaseline="hanging"
            textAnchor="start"
            x={ASM_COL_X}
            y={ASM_COL_TC_Y + ASM_COL_BOX.h + ASM_COL_LABEL_GAP}
          >
            {tcLabel}
            {tcControl === undefined ? null : (
              <tspan x={ASM_COL_X} dy="1.3em">
                {tcControl.label}
              </tspan>
            )}
          </text>
        </g>
      )}
      {gripper ? (
        <>
          {/*
        3축 그리퍼 — **엔드 이펙터만**(염색기 피펫 툴처럼 · eric 2026-10-08 「염색기는 피펫이 움직이는데(엔드포인터 부분만) 그리퍼만 보이게 해줘.
        목적은 TC 잡아서 챔버에다가 넣는 과정을 그리퍼가 보이게」). 하우징 · 빔 · 기둥은 없다. 몸통 + 손가락 둘이 내려온다(`fig-line__head` · `HEAD_TRAVEL`) —
        쥐면 손가락이 오므라들고 사이에 TC(U 자 틀)가 들린다
      */}
          <g
            className="fig-line__gantry"
            data-at={atStage ? 'stage' : 'park'}
            data-part="gantry"
            style={{ transform: `translateX(${String(gx - GANTRY_STAGE_X)}px)` }}
          >
            <g
              className="fig-line__head"
              data-down={down ? 'true' : 'false'}
              data-part="press-head"
            >
              {/* 그리퍼 크기는 장비 판에서 원심분리기 그리퍼와 같은 화면 크기로 맞춘다(`monitoring.css` `.fig-line__rgd` · eric 2026-10-08 「원심분리기 크기 기준으로 통일」) */}
              <g className="fig-line__rgd" data-part="rgd-scale">
                <RgdGripperGlyph down={down} holding={holding} />
                {holding ? (
                  /* 든 TC — 손가락 끝에 물린 U 자 틀 */
                  <path
                    className="fig-line__on"
                    d="M92 70v8h16v-8"
                    data-part="held-tc"
                    style={{ fill: 'none' }}
                  />
                ) : null}
              </g>
              {tcMotion === 'placing' ? (
                /* 장착 — 손가락에서 챔버까지 점선 안내선(내려갈 길) */
                <path
                  className="fig-line__hair"
                  d={`M100 ${String(GRIP_TIP_Y + 8)}V${String(ASM_TC.top - 2)}`}
                  data-part="tc-guide"
                />
              ) : null}
              {arrowDir === undefined ? null : (
                /* 장착 ↓↓ · 분리 ↑↑ — 쐐기 둘(한 개짜리 까딱보다 방향이 읽힌다) */
                <path
                  className="fig-line__hair fig-line__nudge"
                  d={
                    arrowDir === 'down'
                      ? 'M122 40v20M117 50l5 5 5-5M117 56l5 5 5-5'
                      : 'M122 62V42M117 52l5-5 5 5M117 58l5-5 5 5'
                  }
                  data-dir={arrowDir}
                  data-part="press-arrow"
                />
              )}
            </g>
          </g>
        </>
      ) : null}
      {/* 압착 장치 — 위 팔(고정) · 팔 밑 누름판이 내려와 TC 를 누른다(`fig-line__press` · CSS translateY) */}
      <g data-part="press-unit">
        {/* 위 팔(고정) — 오른쪽 기둥은 걷었다(eric 2026-10-08 「오른쪽 사각형 있는 건 빼줘」) */}
        <rect
          className="fig-line__part"
          data-part="press-arm"
          height={6}
          rx={2}
          width={ASM_PRESS_W}
          x={100 - ASM_PRESS_W / 2}
          y={ASM_PRESS_BOTTOM - 18}
        />
        <g
          className="fig-line__press"
          data-down={pressing ? 'true' : 'false'}
          data-part="press-plate"
        >
          <rect
            className="fig-line__part"
            height={6}
            rx={1}
            width={6}
            x={97}
            y={ASM_PRESS_BOTTOM - 12}
          />
          <rect
            className={pressing ? 'fig-line__on' : 'fig-line__part'}
            data-part="press-face"
            height={6}
            rx={2}
            width={ASM_PRESS_W}
            x={100 - ASM_PRESS_W / 2}
            y={ASM_PRESS_BOTTOM - 6}
          />
        </g>
      </g>
      {/* Z 스테이지(정면) — 판(테두리만 · UV 가 아래에서 통과한다) · 받침 둘(가운데는 램프 자리) */}
      <rect
        className="fig-line__part"
        data-part="stage"
        height={ASM_STAGE.h}
        rx={2}
        width={ASM_STAGE.w}
        x={100 - ASM_STAGE.w / 2}
        y={ASM_STAGE_TOP}
      />
      <rect
        className="fig-line__part"
        height={14}
        rx={2}
        width={6}
        x={100 - ASM_STAGE.w / 2}
        y={ASM_STAGE_TOP + ASM_STAGE.h}
      />
      <rect
        className="fig-line__part"
        height={14}
        rx={2}
        width={6}
        x={100 + ASM_STAGE.w / 2 - 6}
        y={ASM_STAGE_TOP + ASM_STAGE.h}
      />
      {/* 두께 조절기(TC) — 챔버를 감싸는 U 자 틀(정면). 챔버에 얹힌 동안 실선 · 분리로 들어 올린 뒤에는 빠져나간 자리(점선) · 장착 중엔 없음 */}
      {tcMotion === 'placing' ? null : (
        <path
          className={tcMotion === 'removing' ? 'fig-line__empty' : press}
          d={`M${String(100 - ASM_TC.w / 2)} ${String(ASM_TC.top)}v${String(ASM_TC.bottom - ASM_TC.top)}h${String(ASM_TC.w)}v${String(ASM_TC.top - ASM_TC.bottom)}`}
          data-part="tc"
          style={{ fill: 'none' }}
        />
      )}
      {chamberSlot === undefined ? null : (
        <g data-part="stage-chamber">
          <rect
            className="fig-line__on"
            data-part="stage-chamber-body"
            height={ASM_CHAMBER.h}
            rx={3}
            width={ASM_CHAMBER.w}
            x={100 - ASM_CHAMBER.w / 2}
            y={ASM_CHAMBER_TOP}
          />
          {/* 🔴 스테이지에 올라간 챔버 = 지금 다루는 챔버 — 테두리가 깜빡인다(트레이 칸과 같은 `fig-tray__blink` · eric 2026-10-15 「어셈블러 기기에 챔버 올라가잖아. 거기도 깜빡거리는 걸로」) */}
          <rect
            className="fig-tray__blink"
            data-part="stage-chamber-blink"
            height={ASM_CHAMBER.h}
            rx={3}
            width={ASM_CHAMBER.w}
            x={100 - ASM_CHAMBER.w / 2}
            y={ASM_CHAMBER_TOP}
          />
          {/* 칸 이름 — 챔버가 작아져 안에 안 든다. TC 틀 오른쪽 바깥(폐기통 앞)에 선다 · 콘솔 장비 판은 늘 세운다(겹침 그리퍼 칩은 2026-10-14 에 걷혔다) */}
          {chamberLabel ? (
            <text
              className="fig-mod__txt fig-line__lab fig-line__lab--sm"
              data-part="stage-chamber-label"
              dominantBaseline="central"
              textAnchor="start"
              x={100 + ASM_TC.w / 2 + 3}
              y={ASM_CHAMBER_TOP + ASM_CHAMBER.h / 2}
            >
              {chamberSlot}
            </text>
          ) : null}
        </g>
      )}
      <text
        className="fig-mod__txt"
        data-part="status"
        textAnchor="middle"
        x={100}
        y={ASM_STATION_BOX.y + ASM_STATION_BOX.h - 4}
      >
        {statusLabel}
      </text>
      {/* 내려온 거리의 집 — CSS 와 같은 수를 DOM 에 적어 두 곳이 어긋나면 시험이 잡는다 */}
      <desc data-head-travel={HEAD_TRAVEL} data-press-travel={PRESS_TRAVEL} />
    </svg>
  )
}

/** 시안 `shakerSVG` 높이(250) + 아래 상태 한 줄 */
const SHAKER_H = 272
/** 정지 자리 세로 중심 — 시안 눈금(후면 52 · 피펫팅 125 · 전면 198). 위가 후면(6축 쪽), 아래가 전면(사람 쪽) */
const LINE_STOP_Y: Readonly<Record<ShakerStop, number>> = { rear: 52, work: 125, front: 198 }
const LINE_STOPS: readonly ShakerStop[] = ['rear', 'work', 'front']
/** 플랫폼 높이 — 이동 범위(20 ~ 230) 안에서 세 자리 어디에 서도 범위를 안 넘는다 */
const PLATFORM_H = 60

/**
 * 염색기 셰이커 — 시안 `shakerSVG`. 둥근 본체 · 이동 범위(점선 옅은 면) · 레일 둘 · 오른쪽 정지 자리 눈금 3 + 이름.
 * 시안은 「셰이커 값 미수신」 그림 하나뿐이다 — 값이 오면 그 위에 플랫폼 · 걸쇠 · 트레이를 얹는다(종전 그림의 표와 같은 뜻).
 *
 * | 값 | 그림 |
 * | --- | --- |
 * | 자리 모름 | 시안 그대로 — 이동 범위 점선만. 걸쇠 · 트레이 없음 |
 * | 자리 | 그 자리에 흰 플랫폼(실선) · 그 눈금이 굵고 이름이 진하다 |
 * | 래치 | 닫힘 = 턱이 안쪽 수평 · 열림 = 세로 · 움직이는 중 = 비스듬 · 모름 = 축만 |
 * | 트레이 | 결착 = 3×3 칸 · 풀림 = 점선 자리 · 모름 = 없음 |
 * | 쉐이킹 | 플랫폼 테 강조색 + 양옆 진동선 |
 */
/** 플랫폼 묶음을 그리는 기준 자리(작업) — 다른 자리는 이 묶음을 CSS `translateY` 로 옮긴다(`figures.css` `.fig-line__carriage`) */
const LINE_BASE_Y = LINE_STOP_Y.work

/**
 * 염색기 셰이커 선 도면 — **플랫폼 묶음(판 · 트레이 · 걸쇠 · 진동선)이 서버 `slider_position` 이 바뀔 때 눈금 사이를 미끄러져 간다**
 * (eric 2026-10-07 「시뮬레이터를 보고 추가할 것」 중 1 · 「움직임」). 묶음은 작업 자리(`LINE_BASE_Y`)에 그려 두고 `translateY(cy − 기준)` 으로
 * 옮긴다 — 값이 바뀌면 토큰 시간(`--dur-move`)만큼 이동. 교반(`SHAKING`)이면 안쪽 묶음이 좌우로 떤다. 자리를 모르면(`stop === null`)
 * 종전대로 묶음 자체를 안 그린다(짐작한 자리에 세우지 않는다).
 * 후문(자동 · 6축 쪽 = 위)은 `door` 가 있을 때 띠로 — 닫힘은 채운 띠 · 열림은 빈 점선 띠 · 글자(`doorLabel`)가 같이 말한다.
 */
export function ShakerLine({
  stop,
  latch,
  tray,
  shaking,
  stopLabels,
  statusLabel,
  label,
  door = null,
  doorLabel,
}: ShakerFigureState & {
  stopLabels: Readonly<Record<ShakerStop, string>>
  statusLabel: string
  label: string
  /** 후문(자동) — 서버 `interlock.doors[].rear.state`. `null` = 미발행(띠는 서되 열림/닫힘을 안 가른다) */
  door?: 'open' | 'closed' | null
  /** 후문 글자(「후문 닫힘」 · 「후문 미발행」) — 부르는 쪽이 사전으로 짓는다 */
  doorLabel?: string | undefined
}) {
  const cy = stop === null ? null : LINE_STOP_Y[stop]
  const by = LINE_BASE_Y

  return (
    <svg
      aria-label={label}
      className={cx('fig fig-mod fig-mod--shaker fig-line', shaking && 'fig-line--shaking')}
      data-latch={latch ?? 'unknown'}
      data-shaking={shaking ? 'true' : 'false'}
      data-stop={stop ?? 'unknown'}
      data-tray={tray ?? 'unknown'}
      height={SHAKER_H}
      role="img"
      viewBox={`0 0 164 ${String(SHAKER_H)}`}
      width={164}
    >
      <rect className="fig-line__body" height={242} rx={12} width={156} x={4} y={4} />
      <rect
        className="fig-line__empty"
        data-part={cy === null ? 'platform-unknown' : 'track'}
        height={210}
        rx={4}
        width={66}
        x={22}
        y={20}
      />
      <rect className="fig-line__part" height={210} width={6} x={20} y={20} />
      <rect className="fig-line__part" height={210} width={6} x={84} y={20} />
      {cy === null ? null : (
        /* 플랫폼 묶음 — 작업 자리에 그려 두고 자리만큼 옮긴다(CSS transition). 안쪽 묶음은 교반 떨림 */
        <g
          className="fig-line__carriage"
          data-part="carriage"
          data-y={cy}
          style={{ transform: `translateY(${String(cy - by)}px)` }}
        >
          <g className="fig-line__shake" data-part="platform-parts">
            <rect
              className="fig-line__on fig-line__platform"
              data-part="platform"
              data-y={cy}
              height={PLATFORM_H}
              rx={4}
              width={58}
              x={26}
              y={by - PLATFORM_H / 2}
            />
            {tray === 'present' ? (
              <g data-part="tray">
                <rect
                  className="fig-line__part"
                  height={45}
                  rx={3}
                  width={45}
                  x={32.5}
                  y={by - 22.5}
                />
                {Array.from({ length: 9 }, (_unused, n) => (
                  <rect
                    className="fig-line__cell"
                    height={11}
                    key={n}
                    rx={2}
                    width={11}
                    x={36.5 + (n % 3) * 13}
                    y={by - 18.5 + Math.floor(n / 3) * 13}
                  />
                ))}
              </g>
            ) : tray === 'absent' ? (
              <rect
                className="fig-line__empty"
                data-part="tray-empty"
                height={45}
                rx={3}
                width={45}
                x={32.5}
                y={by - 22.5}
              />
            ) : null}
            <LineLatch cy={by} shape={latch} side="left" />
            <LineLatch cy={by} shape={latch} side="right" />
            {shaking ? (
              <path
                className="fig-mod__wave fig-line__wave"
                d={`M13 ${String(by - 12)}q-5 12 0 24M97 ${String(by - 12)}q5 12 0 24`}
                data-part="wave"
              />
            ) : null}
          </g>
        </g>
      )}
      {/* 후문(자동) — 위 = 후면(6축 쪽). 닫힘은 채운 띠 · 열림은 빈 점선 띠 · 미발행은 가는 점선 띠(가르지 않는다) */}
      <rect
        className={cx(
          'fig-line__door',
          door === 'open' && 'fig-line__door--open',
          door === null && 'fig-line__door--unknown',
        )}
        data-door={door ?? 'unknown'}
        data-part="door"
        height={5}
        rx={2}
        width={70}
        x={20}
        y={9}
      />
      {doorLabel === undefined ? null : (
        <text
          className="fig-line__note"
          data-part="door-label"
          dominantBaseline="central"
          x={98}
          y={11.5}
        >
          {doorLabel}
        </text>
      )}
      {LINE_STOPS.map((id) => {
        const y = LINE_STOP_Y[id]
        const on = id === stop
        return (
          <g data-stop-mark={id} key={id}>
            <path
              className={cx('fig-line__tick', on && 'fig-line__tick--on')}
              d={`M98 ${String(y)}h8`}
            />
            <text
              className={cx('fig-line__note', on && 'fig-line__note--on')}
              dominantBaseline="central"
              x={112}
              y={y}
            >
              {stopLabels[id]}
            </text>
          </g>
        )
      })}
      <text className="fig-mod__txt" data-part="status" textAnchor="middle" x={82} y={SHAKER_H - 6}>
        {statusLabel}
      </text>
    </svg>
  )
}

/**
 * 염색기 정면도의 «지금» — 서버 시퀀스 · 상태 이름에서 고른다(`screens/monitoring/stainFrontModel.ts` `stainFrontPhase()`).
 * `idle` 올라간 채 · `move` 올라간 채(이동) · `well` 그 챔버로 가서 끝이 닿게 내려옴 · `reagent` 시약 통으로 가서 담금(시약 흡입) ·
 * `waste` 폐기통으로 가서 담금(토출 · 팁 폐기) · `tips` 팁 랙으로 가서 담금(팁 장착) · `aside` 팁 교체 구간인데 동작 이름이 없다(팁 랙 위 · 올라간 채) ·
 * `shake` 평면도 트레이 떨림
 */
export type StainFrontPhase =
  'idle' | 'move' | 'well' | 'tips' | 'reagent' | 'waste' | 'aside' | 'shake'

/** 폐기통에서 하는 일 — 액체 토출(`expel`) · 팁 버림(`discard_tip`). 모르면 `undefined`(통 표현은 토출로 선다) */
export type StainWasteKind = 'liquid' | 'tip'

/**
 * 피펫 툴 svg — **가로는 평면도와 같은 좌표**(`PLAN_WIDTH` · 1 단위 = 화면 1px 라 선 굵기 보정이 없다), 세로 `STAIN_TOOL_H`.
 * 툴은 가운데(`PLAN_CENTER_X`)에 그려 두고 CSS `translate` 로 그 챔버 자리까지 옮긴다 — 아래 변이 평면도 윗변이다
 */
/** 툴 svg 높이 — 막대 + 피펫 몸통 · 노즐 · 팁(eric 2026-10-08 「피펫 튜브 크기 좀 조금만 더 키워줘」 → 「좀 더 키워주고 막대기 뒤에 붙여줘」 · 처음의 약 1.6 배) */
/**
 * 🔴 **여백 규칙**(2026-10-11 · eric 「팁 랙 튜브 랙 폐기통이.. 너무 트레이랑 붙어 있는데.. 여유 있게」) — 부품 사이 · 이름표 틈은 장비 판 공용
 *    기준 px(`figureSpacing.ts`)를 이 그림의 맞춤 높이(272)로 환산한 값이다. 종전엔 팁 랙 · 통 ↔ 트레이 14 · 이름표 11(글자 가운데)로
 *    1920 화면에서 통 ↔ 트레이 9px · 이름표 틈 0.7px 였다(어셈블러는 23 · 3px).
 */
const STAIN_PART_GAP = figUnits(FIG_PART_GAP, STAIN_FRONT_FIT_N)
const STAIN_LABEL_GAP = figUnits(FIG_LABEL_GAP, STAIN_FRONT_FIT_N)
/** 이름표 한 줄의 키(그림 좌표) — 화면 최소 단 12px 한 줄이 가장 작은 판에서 차지하는 키(`FIG_LABEL_LINE`)를 이 그림 단위로 */
const STAIN_LABEL_H = figUnits(FIG_LABEL_LINE, STAIN_FRONT_FIT_N)
/** 올라간 툴의 노즐 끝 · 팁 끝 y(툴 svg 좌표) — 내려올 거리는 「틀 아래 변 − 끝 + 평면도 안 챔버 가운데」 */
const STAIN_NOZZLE_END = 178
const STAIN_TIP_END = 210
/**
 * 툴 svg 높이 — 올라간 팁 끝 ↔ 트레이 몸체(평면도 윗변 + 축 글자 줄 `BODY_TOP` 아래) 세로 틈이 부품 간격(`STAIN_PART_GAP`)이 되게.
 * 종전 216(팁 끝 ↔ 평면도 윗변 6 · 1920 에서 몸체까지 14px)
 */
const STAIN_TOOL_H = STAIN_TIP_END + STAIN_PART_GAP - BODY_TOP
/**
 * 트레이 왼쪽의 팁 랙 — 한 칸, 세로는 트레이 테두리와 같다(eric 2026-10-08 「트레이 왼쪽에 팁랙 부분도 넣어줘」). 평면도 좌표 그대로 —
 * 평면도 왼쪽 밖이라 x 가 음수다(툴 · 랙 svg 의 viewBox 가 그만큼 왼쪽으로 넓다)
 */
const RACK_W = 72
const RACK_GAP = STAIN_PART_GAP
const RACK_X = -(RACK_GAP + RACK_W)
const LEFT_WIDTH = RACK_GAP + RACK_W
/**
 * 피펫 툴 자리의 최소 머무름(ms) — 한 번 간 자리가 눈에 들어온 뒤 다음으로 간다. 서버 동작이 0.5~1.7초마다 바뀌므로(dev2 2026-10-08) 머무름이
 * 그보다 길면 줄(`useDwell`)이 계속 밀린다 → 1.5초에서 0.7초로. 이동은 순간 이동이라(`figures.css`) 머무름이 곧 보이는 시간이다
 */
export const STAIN_DWELL_MS = 700
type StainFrame = {
  phase: StainFrontPhase
  well: string | null
  arrow: 'up' | 'down' | undefined
  waste: StainWasteKind | undefined
}
const stainFrameSame = (a: StainFrame, b: StainFrame) =>
  a.phase === b.phase && a.well === b.well && a.arrow === b.arrow && a.waste === b.waste
/* 줄이 밀리면 이동 · 대기 자리부터 버린다 — 분주 · 흡입 · 폐기 · 팁 장착 자리는 끝까지 보여 준다(eric 2026-10-08 「discard tip 하면 폐기통에 왔다 가야지」) */
const STAIN_DWELL_OPTIONS = {
  skippable: (frame: StainFrame) => frame.phase === 'move' || frame.phase === 'idle',
}
/**
 * 트레이 오른쪽의 통 둘 — **한 열 두 행**: 위 튜브 랙(종전 「시약 랙」 · eric 2026-10-08 이름 바꿈) · 아래 폐기통(eric 2026-10-08 「시약 통, 처리 통 두 개를 간단한 사양형으로 트레이 오른쪽에..
 * 트레이의 높이와 같아야 해」 → 「폐기통을 아래 사각형으로 하고 시약 랙을 위에.. 한 열, 2개의 행으로」). 평면도 좌표 그대로 · 둘을 합친 세로가
 * 트레이 테두리(`BODY_TOP` · `BODY_HEIGHT`)와 같다. 수량 · 액면은 그리지 않는다(소모품 판)
 */
const BIN_GAP_LEFT = STAIN_PART_GAP
const BIN_W = 96
/** 두 통 사이 — 위 통의 이름표(통 바깥 왼쪽 아래)가 위아래 이름표 틈을 두고 들어갈 만큼 */
const BIN_GAP = STAIN_LABEL_GAP * 2 + STAIN_LABEL_H
/** 이름표 — 상자 «바깥» 왼쪽 아래(eric 2026-10-08 「사각형 바깥 아랫쪽 왼쪽에 텍스트」). 상자 아래 변 → 글자 윗변(`hanging`) = 이름표 틈 */
const BIN_LABEL_DY = STAIN_LABEL_GAP
/** 맨 아래 이름표(팁 랙 · 폐기통)가 평면도 아래로 더 그리는 높이 */
const BIN_LABEL_ROOM = Math.max(
  0,
  STAIN_LABEL_GAP + STAIN_LABEL_H - (PLAN_HEIGHT - (BODY_TOP + BODY_HEIGHT)),
)
const DECK_SIDE_H = PLAN_HEIGHT + BIN_LABEL_ROOM
const BINS_WIDTH = BIN_GAP_LEFT + BIN_W
const BIN_X = PLAN_WIDTH + BIN_GAP_LEFT
const BIN_H = (BODY_HEIGHT - BIN_GAP) / 2
const BIN_Y: Readonly<Record<'reagent' | 'waste', number>> = {
  reagent: BODY_TOP,
  waste: BODY_TOP + BIN_H + BIN_GAP,
}
/** 통에 담글 때 끝이 닿는 깊이 — 통 윗변에서 안으로 */
const BIN_DIP = 24
/** 툴 svg 폭 — 팁 랙 + 평면도 + 통 둘(viewBox 는 `-LEFT_WIDTH` 부터) */
const STAIN_W = LEFT_WIDTH + PLAN_WIDTH + BINS_WIDTH
/** 툴 머리 반폭(몸체 40 · 장착 블록이 오른쪽으로 22) — 격자 왼쪽 끝에서 출발할 때 몸체가 격자 안에 들게 */
const STAIN_HEAD_HALF = 24

/**
 * 염색기 장치 칸 — **위에 피펫 툴 정면도, 아래에 종전 L 트레이 평면도**(eric 2026-10-07 「염색기를 정면도로」 → 「가운데에 L 트레이를 넣고..
 * 피펫의 끝이 해당 챔버에 닿게 내리고 올리고 하는 것만」 → 「트레이는 기존대로 평면도로」 · 「3축 틀 안보여줘도 돼」 → 2026-10-08 「해당 피펫 툴이
 * 해당 챔버 위치에 가게 해줘」). 종전 셰이커 평면도(`ShakerLine`)와 왼쪽 트레이 칸을 이 한 장이 대신한다 — 콘솔 장비 판에서만(`FocusFigure` 의 `front`).
 *
 * | 재료 | 그림 |
 * | --- | --- |
 * | `phase === 'well'` + `well` | 툴이 **그 칸 위로**(X = 칸 가로 가운데) 가서 끝이 **그 칸 가운데**까지 내려온다(Z · 행이 아래일수록 깊다) · 몸체 강조색 테 · 흡입 ↑ · 분주 ↓ |
 * | `phase === 'well'` · 칸 모름 | 가운데 올라간 채 — 짐작한 칸에 세우지 않는다 |
 * | `phase === 'reagent'` · `'waste'` | 트레이 오른쪽 위 시약 랙 · 아래 폐기통으로 가서 윗변 안쪽까지 담근다(시약 흡입 · 토출 · 팁 폐기) · 그 통 테 강조 |
 * | `phase === 'tips'` | 트레이 왼쪽 팁 랙으로 가서 담근다(팁 장착) · 랙 테 강조 |
 * | 그 밖(`idle` · `move` · `aside` · `shake`) | 올라간 채(`aside` 는 팁 랙 위) — 무슨 동작인지는 아래 글자 |
 * | `plan` | 종전 트레이 평면도 노드(`FocusFigure` `preTray`) 그대로 — 좌표는 `TrayFigure` 가 내준다(`trayCellAnchor`) |
 * | `tipMounted`(서버 `tip.mounted`) | 참이면 노즐 끝에 팁 원뿔 — 닿는 끝도 그만큼 길다. 거짓 · 모름이면 없음 |
 * | `shaking`(상태 `SHAKING`) | 평면도 트레이가 좌우로 떤다 — 트레이는 셰이커 플랫폼에 결착된 채 같이 흔들린다 |
 * | `stopLabel` · `doorLabel` | 아래 작은 글자 — 정면에서 안 보이는 사실이라 글자로만 |
 *
 * 🔴 움직임은 둘 다 CSS transition(`--dur-move`) — 프레임이 바뀔 때만 옮겨 간다. 3축 틀 · 소모품 · 구역은 그리지 않는다.
 * 🔴 툴이 평면도 위로 내려오므로 툴 svg 가 평면도 **위에** 그려진다(`figures.css` z-index · overflow visible).
 */
/**
 * **TC rack 외곽**(eric 2026-10-19 사진 · 「TC 도 외곽 테두리만」) — 왼쪽 귀만 둥근 판 + 왼쪽 손잡이 구멍. 소모품 판 TC 그림(`DeckRackFigure` `TcPlate`)과
 * 같은 모양 말이고, 칸은 그리지 않는다. 상자 크기에 맞춰 비율로 선다
 */
function TcRackOutline({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  const n = (v: number) => String(Math.round(v * 100) / 100)
  const r = h * 0.18
  const outer = `M${n(x + r)} ${n(y)}H${n(x + w)}V${n(y + h)}H${n(x + r)}A${n(r)} ${n(r)} 0 0 1 ${n(x)} ${n(y + h - r)}V${n(y + r)}A${n(r)} ${n(r)} 0 0 1 ${n(x + r)} ${n(y)}Z`
  const hx = x + w * 0.05
  const hw = w * 0.12
  const hy = y + h * 0.18
  const hh = h * 0.64
  const hr = Math.min(hw, hh) * 0.22
  const hole = `M${n(hx + hr)} ${n(hy)}H${n(hx + hw - hr)}A${n(hr)} ${n(hr)} 0 0 1 ${n(hx + hw)} ${n(hy + hr)}V${n(hy + hh - hr)}A${n(hr)} ${n(hr)} 0 0 1 ${n(hx + hw - hr)} ${n(hy + hh)}H${n(hx + hr)}A${n(hr)} ${n(hr)} 0 0 1 ${n(hx)} ${n(hy + hh - hr)}V${n(hy + hr)}A${n(hr)} ${n(hr)} 0 0 1 ${n(hx + hr)} ${n(hy)}Z`
  return (
    <path
      className="fig-tray__body"
      d={`${outer} ${hole}`}
      data-part="tc-rack-outline"
      fillRule="evenodd"
    />
  )
}

/**
 * **팁 랙 · 튜브 랙 상자의 랙 테두리**(eric 2026-10-19 「팁랙은.. 둥근 원은 하지 말고 테두리만 · 튜브랙도 안에 둥근 원 말고 외곽만」 — 염색기 장비 그림의
 * 상자이지 소모품 판이 아니다). 소모품 판 랙 그림(`RackPlate`)과 같은 모양 말 — 안쪽 모따기 테(팔각) + 좌우 긴 변 가운데 반원 홈 · 튜브 랙만 귀 나사 넷.
 * 구멍(팁 · 튜브 원)은 그리지 않는다 — 칸 수 · 상태는 소모품 판이 말한다.
 */
function RackOutline({
  x,
  y,
  w,
  h,
  screws,
}: {
  x: number
  y: number
  w: number
  h: number
  screws: boolean
}) {
  const inset = 6
  const chamfer = 7
  const notch = 4
  const x0 = x + inset
  const x1 = x + w - inset
  const y0 = y + inset
  const y1 = y + h - inset
  const ym = y + h / 2
  const n = (v: number) => String(Math.round(v * 100) / 100)
  const pocket = [
    `M${n(x0 + chamfer)} ${n(y0)}`,
    `H${n(x1 - chamfer)}`,
    `L${n(x1)} ${n(y0 + chamfer)}`,
    `V${n(ym - notch)}`,
    `A${n(notch)} ${n(notch)} 0 0 1 ${n(x1)} ${n(ym + notch)}`,
    `V${n(y1 - chamfer)}`,
    `L${n(x1 - chamfer)} ${n(y1)}`,
    `H${n(x0 + chamfer)}`,
    `L${n(x0)} ${n(y1 - chamfer)}`,
    `V${n(ym + notch)}`,
    `A${n(notch)} ${n(notch)} 0 0 1 ${n(x0)} ${n(ym - notch)}`,
    `V${n(y0 + chamfer)}`,
    'Z',
  ].join(' ')
  const s = 3.4
  return (
    <g data-part="rack-outline" data-screws={screws ? 'true' : 'false'}>
      <path className="fig-stainfront__rack-pocket" d={pocket} />
      {screws
        ? (
            [
              [x + s, y + s],
              [x + w - s, y + s],
              [x + s, y + h - s],
              [x + w - s, y + h - s],
            ] as const
          ).map(([cx0, cy0]) => (
            <circle
              className="fig-stainfront__rack-screw"
              cx={n(cx0)}
              cy={n(cy0)}
              key={`${n(cx0)}-${n(cy0)}`}
              r={1.6}
            />
          ))
        : null}
    </g>
  )
}

/**
 * **염색기 폐기통 — 위에서 본 깔때기, 단순형**(eric 2026-10-19 사진 「위에서 찍은 것처럼」 → 「단순화해줘」 → 「사각형 중앙에 그냥 하나만」).
 * 통 가운데 바닥 구멍 하나(짙은 면) + 통 네 귀에서 구멍 네 귀로 내려가는 깔때기 사선 넷. 칸 나눔 · 벽 선 · 나사는 그리지 않는다.
 */
function WasteBinTop({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  /* 🔴 염색기 폐기통(96 폭)이 기준 — 테 두께 · 구멍 모서리를 상자 폭에 비례시켜 어셈블러 폐기통도 같은 모양으로 선다(eric 2026-10-19 「염색기 기준으로 폐기통은 맞춰」) */
  const unit = w / BIN_W
  const rim = 3.5 * unit
  const left = x + rim
  const right = x + w - rim
  const top = y + rim
  const bottom = y + h - rim
  const r2 = (v: number) => Math.round(v * 100) / 100
  /* 가운데 바닥 구멍 하나(eric 2026-10-19 「사각형 중앙에 그냥 하나만」) — 통 폭 · 높이의 가운데 0.36 · 0.4 */
  const one = {
    x: r2(left + (right - left) * 0.32),
    y: r2(top + (bottom - top) * 0.3),
    w: r2((right - left) * 0.36),
    h: r2((bottom - top) * 0.4),
  }
  return (
    <g data-part="waste-top">
      {[{ x0: left, x1: right }].map(({ x0, x1 }, n) => (
        <g data-part="waste-room" key={n}>
          {/*
           * 깔때기 벽 — 칸 네 귀에서 바닥 구멍 네 귀로 가는 사선(eric 2026-10-19 「빠져 나가는 느낌이 들게.. 사선 선을 그어줘. 안쪽 사각형이
           * 들어가는 방향이란게 안보여」). 보조선 굵기 · 옅은 색이라 구멍(짙은 면)이 앞에 선다
           */}
          {(
            [
              [x0, top, one.x, one.y],
              [x1, top, one.x + one.w, one.y],
              [x1, bottom, one.x + one.w, one.y + one.h],
              [x0, bottom, one.x, one.y + one.h],
            ] as const
          ).map(([ax, ay, bx, by], k) => (
            <line
              className="fig-stainfront__bin-wall"
              key={k}
              x1={r2(ax)}
              x2={r2(bx)}
              y1={r2(ay)}
              y2={r2(by)}
            />
          ))}
          <rect
            className="fig-stainfront__bin-hole"
            height={one.h}
            rx={1.5 * unit}
            width={one.w}
            x={one.x}
            y={one.y}
          />
        </g>
      ))}
    </g>
  )
}

/**
 * **통 · 랙 안의 «부분» 피펫 표현**(eric 2026-10-18 「피펫을 넣진 말고.. 팁랙을 끼울 때 팁이 보이면서 끼는 느낌 · 폐기통에 버릴 때 버리는 느낌 ·
 * 워시액이나.. 가지고 가는 느낌 · 피펫 전부 다 말고.. 부분적으로」). 피펫 전체(툴)를 끈 콘솔 장비 판에서만 선다 — 그 자리의 노즐 끝 한 토막 + 팁 하나.
 * - `mount` 팁 랙: 랙에 선 팁 위로 노즐이 내려와 끼우고 같이 올라간다
 * - `draw` 튜브 랙: 팁이 액면 아래로 담갔다가 팁 안이 차오르며 올라간다
 * - `expel` 폐기통(토출): 팁 안 액체가 줄며 방울이 떨어진다
 * - `discard` 폐기통(팁 버림): 팁이 노즐에서 빠져 기울며 떨어진다
 * 🔴 서버가 말한 동작(`stainFrontPhase` · 폐기 종류)만 그린다 — 언제 끝나는지 모르므로 한 박자를 되풀이한다. 모션 줄임이면 `--dur-move` 0 이라 멈춘 한 장
 */
type BinCueKind = 'mount' | 'draw' | 'expel' | 'discard'
function BinCue({
  cue,
  x,
  y,
  w,
  h,
}: {
  cue: BinCueKind
  x: number
  y: number
  w: number
  h: number
}) {
  const mx = x + w / 2
  /* 팁 — 위 폭 11 · 아래 3 · 길이는 상자 키의 0.55(상자 70 → 39 · 화면 1920 에서 약 24px) */
  const len = Math.round(h * 0.55)
  const top = y + 6
  const half = (f: number) => 5.5 - 4 * f
  const at = (f: number) => top + len * f
  const tip = `M${String(mx - 5.5)} ${String(top)}H${String(mx + 5.5)}L${String(mx + 1.5)} ${String(at(1))}H${String(mx - 1.5)}Z`
  const liquid = `M${String(mx - half(0.4))} ${String(at(0.4))}H${String(mx + half(0.4))}L${String(mx + 1.5)} ${String(at(1))}H${String(mx - 1.5)}Z`
  /* 담그는 거리 — 노즐 · 팁이 내려오는 몫(상자 키의 0.2) */
  const dip = Math.round(h * 0.2)
  return (
    <g
      className={`fig-stainfront__cue fig-stainfront__cue--${cue}`}
      data-cue={cue}
      data-part="bin-cue"
      style={{ '--cue-dip': `${String(dip)}px` } as CSSProperties}
    >
      {cue === 'draw' ? (
        /* 액면 — 팁 끝이 담기는 높이(내려온 팁 끝보다 조금 위) */
        <line
          className="fig-stainfront__cue-surface"
          x1={x + 10}
          x2={x + w - 10}
          y1={at(1) + dip - 7}
          y2={at(1) + dip - 7}
        />
      ) : null}
      <g className="fig-stainfront__cue-tool">
        <rect
          className="fig-stainfront__cue-nozzle"
          height={14}
          rx={1.5}
          width={11}
          x={mx - 5.5}
          y={top - 14}
        />
        <g className="fig-stainfront__cue-tip">
          <path className="fig-stainfront__cue-tipbody" d={tip} />
          {cue === 'draw' || cue === 'expel' ? (
            <path className="fig-stainfront__cue-liquid" d={liquid} />
          ) : null}
        </g>
      </g>
      {cue === 'expel'
        ? [0, 1].map((i) => (
            <circle
              className="fig-stainfront__cue-drop"
              cx={mx}
              cy={at(1) + 4}
              key={i}
              r={1.8}
              style={{ animationDelay: `calc(var(--dur-move) * ${String(i)})` }}
            />
          ))
        : null}
    </g>
  )
}

/**
 * 모듈 앱 소모품 판(`ModuleSupplyStrip`)이 받는 염색기의 «지금» — 데크를 넘겨받은 판(`renderDeck`)은 팁 랙 · 튜브 랙을 자기가 그리므로,
 * 종전 데크가 그 랙에 걸던 강조(깜빡이는 테 · 부분 피펫 `BinCue`)를 판이 같은 모양으로 걸 수 있게 넘긴다.
 * `phase` · `waste` 는 머무름(`STAIN_DWELL_MS`)을 거친 값 — 데크의 폐기통과 같은 박자다
 */
export interface StainDeckNow {
  phase: StainFrontPhase
  waste: StainWasteKind | undefined
  /** 그림 안 피펫 툴이 켜졌나 — 켜졌으면 부분 피펫(`BinCue`)을 안 그린다(종전 데크와 같다) */
  tool: boolean
}

/**
 * 랙 한 장 위의 «지금 다룸» 표지 — 종전 데크의 팁 랙 · 튜브 랙과 같은 `fig-tray__blink` 테 + (툴을 끈 판이면) 부분 피펫.
 * 랙 그림(`DeckRackFigure overlay`) 좌표에 그대로 얹는다. 콘솔 스킨의 선 굵기 · 색 규칙이 `.fig.fig-line` 아래에서만 서서 그 틀로 감싼다
 */
export function StainBinNow({
  now,
  kind,
  width,
  height,
}: {
  now: StainDeckNow
  kind: 'tips' | 'reagent'
  width: number
  height: number
}) {
  if (now.phase !== kind) return null
  return (
    <svg
      aria-hidden="true"
      className="fig fig-line"
      data-part="rack-now"
      data-bin={kind}
      height={height}
      overflow="visible"
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      width={width}
    >
      <rect
        className="fig-tray__blink"
        data-part="bin-blink"
        height={height}
        rx={6}
        width={width}
        x={0}
        y={0}
      />
      {now.tool ? null : (
        <BinCue cue={kind === 'tips' ? 'mount' : 'draw'} h={height} w={width} x={0} y={0} />
      )}
    </svg>
  )
}

export function StainingFrontLine({
  phase: phaseNow,
  well: wellNow,
  arrow: arrowNow,
  waste: wasteNow,
  tipMounted,
  shaking,
  plan,
  binLabels,
  stopLabel,
  doorLabel,
  statusLabel,
  label,
  tool = true,
  renderDeck,
}: {
  phase: StainFrontPhase
  /** 지금 다루는 챔버 칸 이름(`A1` … `C3`). `null` = 모름 — 툴을 짐작한 칸에 세우지 않는다 */
  well: string | null
  /**
   * 랙 · 통 이름(「팁 랙 1」 · 「튜브 랙」 · 「폐기통」) — 부르는 쪽이 사전으로 짓는다.
   * `tips2` 를 주면 팁 랙이 위아래 두 장으로 선다(레시피 팁 랙 `tips_1000_a` · `tips_1000_b` · eric 2026-10-12 「팁 랙 두 개로 상하로 나눠서」)
   */
  binLabels: Readonly<Record<'tips' | 'reagent' | 'waste', string>> & { tips2?: string | undefined }
  /** 액체 방향 — 흡입 ↑ · 분주 ↓ · 그 밖 없음. 내려와 있을 때만 그린다 */
  arrow?: 'up' | 'down' | undefined
  /** 폐기통에서 하는 일(토출 · 팁 버림) — 툴을 끈 판의 통 표현(`BinCue`)이 고른다 */
  waste?: StainWasteKind | undefined
  /** 서버 `tip.mounted`. `null` = 칸 부재(모름) */
  tipMounted: boolean | null
  shaking: boolean
  /** 아래에 설 트레이 평면도 노드(축 있는 콘솔 꼴 · `TrayFigure chamberOutline`) */
  renderDeck?: ((plan: ReactNode, waste: ReactNode, now: StainDeckNow) => ReactNode) | undefined
  plan: ReactNode
  /** 슬라이드 자리 글자(「자리 피펫팅」) — 자리를 모르면 안 넘긴다 */
  stopLabel?: string | undefined
  doorLabel?: string | undefined
  /** 동작 이름 · 스텝 — 접근 이름(aria)에만 쓴다. 화면 글자 줄은 걷었다(eric 2026-10-08 「이동 · 1044/… 이런 거 빼줘」) */
  statusLabel: string
  label: string
  /**
   * 그림 안 피펫 툴을 그릴까. 🔴 기본 `true` = 종전 그대로. 콘솔 장비 판만 끈다 — 판 위 겹침 3축 도구(`StainPipette` · 원심 · 어셈블러와 같은
   * `SheetGripper`)가 피펫을 들고 다닌다(eric 2026-10-13 「염색기를 원심분리기 어셈블러와 같이.. 초기 움직임을 줘」). 끄면 툴 자리(세로 `STAIN_TOOL_H`)도
   * 안 그린다 — 그림이 평면도 높이만큼으로 줄어 칸에 다른 장비와 같은 높이로 맞는다(eric 2026-10-14 「염색기도 높이가 안 맞아」)
   */
  tool?: boolean
}) {
  /*
   * 🔴 최소 머무름 — 서버 동작이 1초 안팎으로 바뀌어 툴이 너무 빨리 오가면 안 보인다(eric 2026-10-08 「너무 빨라서 안 보인다는 거야? 딜레이 줄 수 있어?」).
   *    서버가 말한 자리를 `STAIN_DWELL_MS` 씩은 보여 준 뒤 다음 자리로(그 사이 여러 번 바뀌면 마지막 자리로). 지어내지 않는다 — 조금 늦게 보여 줄 뿐
   */
  const shown = useDwell(
    { phase: phaseNow, well: wellNow, arrow: arrowNow, waste: wasteNow },
    STAIN_DWELL_MS,
    stainFrameSame,
    STAIN_DWELL_OPTIONS,
  )
  const { phase, well, arrow, waste } = shown
  /* 끝이 닿을 자리 — 챔버(그 칸 가운데) · 시약 통 · 폐기통(통 윗변 안쪽). 없으면 올라간 채 */
  const target =
    phase === 'well' && well !== null
      ? cellAnchor(well)
      : phase === 'reagent' || phase === 'waste'
        ? { x: BIN_X + BIN_W / 2, y: BIN_Y[phase] + BIN_DIP }
        : phase === 'tips'
          ? { x: RACK_X + RACK_W / 2, y: BODY_TOP + BIN_DIP }
          : undefined
  const down = target !== undefined
  const end = tipMounted === true ? STAIN_TIP_END : STAIN_NOZZLE_END
  const cx0 = PLAN_CENTER_X
  /*
   * 이동(`move`) 중에는 **마지막으로 간 자리 위에 올라간 채** — 동작 사이마다 가운데로 튀면 왔다갔다로 보인다(eric 2026-10-08 「팁랙을 놓고 폐기통에
   * 버리고가 반복되는 거야?」 — 실제 차례는 시약 랙 → 챔버 → 시약 랙 …). 렌더 중 파생 상태 갱신(React 허용 패턴)
   */
  const [lastX, setLastX] = useState<number | undefined>(undefined)
  if (target !== undefined && target.x !== lastX) setLastX(target.x)
  /* `aside`(팁 교체 구간 · 동작 이름 없음)는 팁 랙 위에 올라간 채 */
  const headX =
    target?.x ?? (phase === 'aside' ? RACK_X + RACK_W / 2 : phase === 'move' ? (lastX ?? cx0) : cx0)
  const drop = target === undefined ? 0 : STAIN_TOOL_H - end + target.y
  const notes = [doorLabel, stopLabel].filter((note): note is string => note !== undefined)
  /*
   * 처음 들어올 때 출발 자리 — 장비 판 그림 격자(`.eqsheet__figs`)의 왼쪽 끝. 원심 · 어셈블러 그리퍼가 격자 왼쪽 위에서 제 자리로 오는 것과 같은 출발점
   * (eric 2026-10-12 「염색기도 동일하게.. 왼쪽에서 쭉 오는 효과」). 칸 하나라 툴 그림이 격자 가운데에 서므로 그림 안 좌표로는 모자란다 —
   * 붙은 직후 한 번 재어 그림 단위로 옮긴다. 격자 밖(다른 자리 · 시험)이면 인라인 기본값(그림 왼쪽 끝)
   */
  const headRef = useRef<SVGGElement>(null)
  useLayoutEffect(() => {
    const head = headRef.current
    const svg = head?.ownerSVGElement
    const figs = head?.closest('.eqsheet__figs')
    if (head == null || svg == null || figs == null) return
    const box = svg.getBoundingClientRect()
    if (box.width === 0) return
    const unit = box.width / STAIN_W
    const fromLeft = (box.left - figs.getBoundingClientRect().left) / unit
    head.style.setProperty(
      '--pip-from',
      `${String(Math.round(-(fromLeft + LEFT_WIDTH + PLAN_CENTER_X) + STAIN_HEAD_HALF))}px`,
    )
  }, [])
  const bins = (wasteOnly: boolean) => (
    <svg
      aria-hidden="true"
      className="fig fig-line fig-stainfront__bins"
      data-part="bins"
      height={DECK_SIDE_H}
      viewBox={
        wasteOnly
          ? `${String(BIN_X - 5)} ${String(BIN_Y.waste - 5)} ${String(BIN_W + 10)} ${String(BIN_H + BIN_LABEL_DY + 28)}`
          : `${String(PLAN_WIDTH)} 0 ${String(BINS_WIDTH)} ${String(DECK_SIDE_H)}`
      }
      width={BINS_WIDTH}
    >
      {(wasteOnly ? (['waste'] as const) : (['reagent', 'waste'] as const)).map((kind) => (
        <g data-bin={kind} data-now={phase === kind ? 'true' : undefined} key={kind}>
          {/* 테는 트레이 테두리와 같은 선(`fig-tray__body` · 굵기 · 색 — eric 2026-10-08 「트레이 굵기와 색과 동일하게」) · 담그는 중이면 강조 */}
          <rect
            className={cx('fig-tray__body', phase === kind && 'fig-line__on--now')}
            height={BIN_H}
            rx={6}
            width={BIN_W}
            x={BIN_X}
            y={BIN_Y[kind]}
          />
          {kind === 'waste' ? (
            <WasteBinTop h={BIN_H} w={BIN_W} x={BIN_X} y={BIN_Y.waste} />
          ) : (
            <RackOutline h={BIN_H} screws w={BIN_W} x={BIN_X} y={BIN_Y.reagent} />
          )}
          {/* 담그는 중이면 챔버 칸과 같은 굵은 테마색 테가 깜빡인다(eric 2026-10-18 「두께가 얇아서.. 진행중인지 알 수가 없어」) */}
          {phase === kind ? (
            <rect
              className="fig-tray__blink"
              data-part="bin-blink"
              height={BIN_H}
              rx={6}
              width={BIN_W}
              x={BIN_X}
              y={BIN_Y[kind]}
            />
          ) : null}
          {phase === kind && !tool ? (
            <BinCue
              cue={kind === 'reagent' ? 'draw' : waste === 'tip' ? 'discard' : 'expel'}
              h={BIN_H}
              w={BIN_W}
              x={BIN_X}
              y={BIN_Y[kind]}
            />
          ) : null}
          {/* 이름표 — 통 «바깥» 왼쪽 아래 · 작은 글자(eric 2026-10-08 · 팁 랙과 같은 자리) */}
          <text
            className="fig-line__note"
            data-part="bin-label"
            dominantBaseline="hanging"
            textAnchor={wasteOnly ? 'middle' : 'start'}
            x={wasteOnly ? BIN_X + BIN_W / 2 : BIN_X}
            y={BIN_Y[kind] + BIN_H + BIN_LABEL_DY}
          >
            {binLabels[kind]}
          </text>
        </g>
      ))}
    </svg>
  )
  return (
    <div
      aria-label={statusLabel === '' ? label : `${label} — ${statusLabel}`}
      className={cx('fig-stainfront', shaking && 'fig-stainfront--shaking')}
      data-down={down ? 'true' : 'false'}
      data-phase={phase}
      data-shaking={shaking ? 'true' : 'false'}
      data-tip={tipMounted === null ? 'unknown' : tipMounted ? 'on' : 'off'}
      data-view="front"
      data-target={phase === 'reagent' || phase === 'waste' || phase === 'tips' ? phase : undefined}
      data-well={phase === 'well' ? (target === undefined ? 'unknown' : well) : 'none'}
      role="group"
    >
      {/*
        피펫 툴(정면) — 바깥 묶음이 칸 자리(X), 안쪽 묶음이 내려감(Z).
        🔴 툴을 끄면(콘솔 장비 판) 툴 자리 svg 를 «아예» 안 그린다(eric 2026-10-14 「염색기도 높이가 안 맞아」) — 종전엔 빈 자리(세로 `STAIN_TOOL_H`)를 남겨
           그림이 칸 아래로 처졌다. 판 위 겹침 피펫도 같은 날 걷혀(실기에서 자리를 모른다) 그 자리를 쓸 것이 없다.
      */}
      {!tool ? null : (
        <svg
          aria-hidden="true"
          className="fig fig-mod fig-mod--shaker fig-line fig-stainfront__tool"
          data-part="pip-tool"
          data-view="front"
          height={STAIN_TOOL_H}
          viewBox={`${String(-LEFT_WIDTH)} 0 ${String(STAIN_W)} ${String(STAIN_TOOL_H)}`}
          width={STAIN_W}
        >
          {tool ? (
            <g
              ref={headRef}
              className="fig-stainfront__head"
              data-part="pip-head"
              data-x={headX}
              style={
                {
                  transform: `translateX(${String(headX - cx0)}px)`,
                  /* 처음 들어올 때 출발 자리 — 그림 왼쪽 끝(팁 랙 바깥 · `figures.css` fig-stain-tool-in) */
                  '--pip-from': `${String(-LEFT_WIDTH - cx0)}px`,
                } as CSSProperties
              }
            >
              <g
                className="fig-stainfront__z"
                data-down={down ? 'true' : 'false'}
                data-drop={drop}
                data-part="pip-z"
                style={{ transform: `translateY(${String(drop)}px)` }}
              >
                {/*
                Hamilton ZEUS LT 정면(eric 2026-10-08 「이게 우리가 쓰는 피펫 툴이야. 이렇게 보이게.. 너무 디테일하게는 말고 정면에서」 · 실물 17 × 100 × 283 mm).
                뒤 위로 비켜 선 장착 블록(Z 축에 물린다) · 위 커넥터 · 세로로 긴 본체 · 상태 표시등 · 아래 고리 · 가는 배럴 · 팁(장착 때만).
              */}
                <rect
                  className="fig-line__part"
                  data-part="pip-arm"
                  height={24}
                  rx={3}
                  width={24}
                  x={cx0 - 2}
                  y={0}
                />
                <rect
                  className="fig-line__part"
                  data-part="pip-connector"
                  height={8}
                  rx={1}
                  width={6}
                  x={cx0 - 14}
                  y={16}
                />
                <rect
                  className={down ? 'fig-line__on fig-stainfront__active' : 'fig-line__part'}
                  data-part="pip-body"
                  height={96}
                  rx={5}
                  width={40}
                  x={cx0 - 20}
                  y={24}
                />
                {/* 상태 표시등 — 실물의 파란 등. 동작 중(내려옴)이면 강조색으로 채운다 · 아니면 테만 */}
                <circle
                  className={
                    down ? 'fig-stainfront__led fig-stainfront__led--on' : 'fig-line__part'
                  }
                  cx={cx0 - 9}
                  cy={78}
                  data-part="pip-led"
                  r={4}
                />
                <rect
                  className="fig-line__part"
                  data-part="pip-collar"
                  height={8}
                  rx={1}
                  width={14}
                  x={cx0 - 7}
                  y={120}
                />
                <rect
                  className="fig-line__part"
                  data-part="nozzle"
                  height={50}
                  rx={2}
                  width={10}
                  x={cx0 - 5}
                  y={128}
                />
                {tipMounted === true ? (
                  <path
                    className="fig-line__solid"
                    d={`M${String(cx0 - 5)} ${String(STAIN_NOZZLE_END)}l5 32 5-32z`}
                    data-part="tip"
                  />
                ) : null}
                {arrow === undefined || !down ? null : (
                  <path
                    className="fig-line__hair fig-line__nudge"
                    d={
                      arrow === 'up'
                        ? `M${String(cx0 + 34)} 110V44M${String(cx0 + 26)} 52l8-8 8 8`
                        : `M${String(cx0 + 34)} 44v66M${String(cx0 + 26)} 102l8 8 8-8`
                    }
                    data-dir={arrow}
                    data-part="flow-arrow"
                  />
                )}
              </g>
            </g>
          ) : null}
        </svg>
      )}
      {/* 데크 — 왼쪽 L 트레이 평면도(종전 그림 그대로 · 교반이면 떤다) · 오른쪽 시약 통 · 폐기통(트레이 높이) */}
      {renderDeck !== undefined ? (
        renderDeck(plan, bins(true), { phase, waste, tool })
      ) : (
        <div className="fig-stainfront__deck" data-part="deck">
          <svg
            aria-hidden="true"
            className="fig fig-line fig-stainfront__bins fig-stainfront__rack"
            data-part="rack"
            height={DECK_SIDE_H}
            viewBox={`${String(-LEFT_WIDTH)} 0 ${String(LEFT_WIDTH)} ${String(DECK_SIDE_H)}`}
            width={LEFT_WIDTH}
          >
            {binLabels.tips2 === undefined ? (
              <g data-bin="tips" data-now={phase === 'tips' ? 'true' : undefined}>
                <rect
                  className={cx('fig-tray__body', phase === 'tips' && 'fig-line__on--now')}
                  height={BODY_HEIGHT}
                  rx={6}
                  width={RACK_W}
                  x={RACK_X}
                  y={BODY_TOP}
                />
                <RackOutline h={BODY_HEIGHT} screws={false} w={RACK_W} x={RACK_X} y={BODY_TOP} />
                {phase === 'tips' ? (
                  <rect
                    className="fig-tray__blink"
                    data-part="bin-blink"
                    height={BODY_HEIGHT}
                    rx={6}
                    width={RACK_W}
                    x={RACK_X}
                    y={BODY_TOP}
                  />
                ) : null}
                {phase === 'tips' && !tool ? (
                  <BinCue cue="mount" h={BODY_HEIGHT} w={RACK_W} x={RACK_X} y={BODY_TOP} />
                ) : null}
                <text
                  className="fig-line__note"
                  data-part="bin-label"
                  dominantBaseline="hanging"
                  textAnchor="start"
                  x={RACK_X}
                  y={BODY_TOP + BODY_HEIGHT + BIN_LABEL_DY}
                >
                  {binLabels.tips}
                </text>
              </g>
            ) : (
              /*
               * 팁 랙 두 장 — 위아래 한 열(오른쪽 튜브 랙 · 폐기통과 같은 칸 높이 · 같은 틈). 툴은 위 랙(1)에 담근다 —
               * 🔴 어느 랙에서 집는지는 서버 걸음이 말하지 않는다(위치 칸이 없다). 강조는 담그는 칸(위)에만
               */
              <g data-bin="tips" data-now={phase === 'tips' ? 'true' : undefined}>
                {(
                  [
                    [BIN_Y.reagent, binLabels.tips, '1'],
                    [BIN_Y.waste, binLabels.tips2, '2'],
                  ] as const
                ).map(([y, name, rack]) => (
                  <g data-rack={rack} key={rack}>
                    <rect
                      className={cx(
                        'fig-tray__body',
                        phase === 'tips' && rack === '1' && 'fig-line__on--now',
                      )}
                      height={BIN_H}
                      rx={6}
                      width={RACK_W}
                      x={RACK_X}
                      y={y}
                    />
                    <RackOutline h={BIN_H} screws={false} w={RACK_W} x={RACK_X} y={y} />
                    {phase === 'tips' && rack === '1' ? (
                      <rect
                        className="fig-tray__blink"
                        data-part="bin-blink"
                        height={BIN_H}
                        rx={6}
                        width={RACK_W}
                        x={RACK_X}
                        y={y}
                      />
                    ) : null}
                    {phase === 'tips' && rack === '1' && !tool ? (
                      <BinCue cue="mount" h={BIN_H} w={RACK_W} x={RACK_X} y={y} />
                    ) : null}
                    <text
                      className="fig-line__note"
                      data-part="bin-label"
                      dominantBaseline="hanging"
                      textAnchor="start"
                      x={RACK_X}
                      y={y + BIN_H + BIN_LABEL_DY}
                    >
                      {name}
                    </text>
                  </g>
                ))}
              </g>
            )}
          </svg>
          <div className="fig-stainfront__plan" data-part="plan">
            {plan}
          </div>
          {bins(false)}
        </div>
      )}
      {notes.length === 0 ? null : (
        <div className="fig-stainfront__notes" data-part="notes">
          {doorLabel === undefined ? null : <span data-part="door-label">{doorLabel}</span>}
          {stopLabel === undefined ? null : <span data-part="stop-label">{stopLabel}</span>}
        </div>
      )}
    </div>
  )
}

/** 걸쇠 하나 — 레일 안쪽 모서리의 축(원) + 턱(굵은 짧은 선). 🔴 상태는 **턱의 방향**이 말한다 */
function LineLatch({
  cy,
  shape,
  side,
}: {
  cy: number
  shape: ShakerLatchShape | null
  side: 'left' | 'right'
}) {
  const x = side === 'left' ? 26 : 84
  const inward = side === 'left' ? 1 : -1
  const jaw =
    shape === 'closed'
      ? `M${String(x)} ${String(cy)}h${String(7 * inward)}`
      : shape === 'open'
        ? `M${String(x)} ${String(cy)}v-10`
        : shape === 'moving'
          ? `M${String(x)} ${String(cy)}l${String(6 * inward)} -6`
          : null
  return (
    <g data-latch-side={side}>
      {jaw === null ? null : <path className="fig-line__jaw" d={jaw} />}
      <circle className="fig-line__pivot" cx={x} cy={cy} r={2.5} />
    </g>
  )
}
